import { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { Calendar, ExternalLink, FileText, Info, ListChecks, MapPin } from '@/components/ui/lucide-memo';
import {
  LeasingMarketSignalDTO,
  MarketSignalInventoryEntityState,
  MarketSignalRuntimeState,
} from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { formatMarketSignalRent, formatMarketSignalSurface } from '@/lib/leasing/marketSignalDisplay';
import { EntityPageTabs } from '@/components/portal/EntityPageTabs';
import { EntitySkeleton } from '../EntityView';

interface MarketSignalPageProps {
  signalId: string;
  className?: string;
  hasEntityRowAbove?: boolean;
}
type MarketSignalWorkspaceTab = 'FOUNDATION' | 'LISTING' | 'INTEGRITY';

const MARKET_SIGNAL_PAGE_TABS = new Set<MarketSignalWorkspaceTab>([
  'FOUNDATION',
  'LISTING',
  'INTEGRITY',
]);

const normalizeMarketSignalWorkspaceTab = (value: unknown): MarketSignalWorkspaceTab | undefined =>
  typeof value === 'string' && MARKET_SIGNAL_PAGE_TABS.has(value as MarketSignalWorkspaceTab)
    ? (value as MarketSignalWorkspaceTab)
    : undefined;

const translations = {
  en: {
    foundation: 'Foundation',
    listing: 'Listing',
    integrity: 'Integrity',
    unknown: 'Unknown',
    generalInfo: 'General Information',
    headline: 'Headline',
    address: 'Address',
    availability: 'Availability',
    immediate: 'Immediate',
    listingDescription: 'Listing Description',
    noDescription: 'No description available for this signal.',
    amenitiesFeatures: 'Amenities & Features',
    noAmenities: 'No amenities listed',
    dataIntegrity: 'Data Integrity',
    sourceLabel: 'Source',
    capturedLabel: 'Captured',
    refLabel: 'Ref',
    cityLabel: 'City',
    statusLabel: 'Status',
    surfaceLabel: 'Surface',
    rentLabel: 'Rent',
    keyFacts: 'Key Facts',
    visitListing: 'Visit Original Listing',
    notAvailable: '-',
  },
  fr: {
    foundation: 'Fondation',
    listing: 'Annonce',
    integrity: 'Integrite',
    unknown: 'Inconnu',
    generalInfo: 'Informations generales',
    headline: 'Titre',
    address: 'Adresse',
    availability: 'Disponibilite',
    immediate: 'Immediat',
    listingDescription: "Description de l'annonce",
    noDescription: 'Aucune description disponible pour ce signal.',
    amenitiesFeatures: 'Services et equipements',
    noAmenities: 'Aucun equipement liste',
    dataIntegrity: 'Integrite des donnees',
    sourceLabel: 'Source',
    capturedLabel: 'Capture',
    refLabel: 'Ref',
    cityLabel: 'Ville',
    statusLabel: 'Statut',
    surfaceLabel: 'Surface',
    rentLabel: 'Loyer',
    keyFacts: 'Faits cles',
    visitListing: "Voir l'annonce originale",
    notAvailable: '-',
  },
};

const parseAmenities = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).filter((entry) => entry.trim().length > 0);
  }
  return [];
};

function MarketSignalFoundationTab({
  signal,
  t,
  locale,
}: {
  signal: LeasingMarketSignalDTO;
  t: typeof translations.en;
  locale: string;
}) {
  return (
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <div className="mb-4 text-[11px] font-semibold text-white/40">{t.generalInfo}</div>
      <div className="space-y-2 text-sm">
        <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
          <span className="text-white/45">{t.headline}</span>
          <span className="text-right text-white/90">{signal.listingHeadline || t.notAvailable}</span>
        </div>
        <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
          <span className="text-white/45">{t.address}</span>
          <span className="text-right text-white/90">{signal.propertyAddress || t.notAvailable}</span>
        </div>
        <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
          <span className="text-white/45">{t.availability}</span>
          <span className="text-right text-white/90">{signal.availabilityDate || t.immediate}</span>
        </div>
        <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
          <span className="text-white/45">{t.surfaceLabel}</span>
          <span className="text-right text-white/90">{formatMarketSignalSurface(signal, t.notAvailable)}</span>
        </div>
        <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
          <span className="text-white/45">{t.rentLabel}</span>
          <span className="text-right text-white/90">{formatMarketSignalRent(signal, locale, t.notAvailable)}</span>
        </div>
        <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
          <span className="text-white/45">{t.cityLabel}</span>
          <span className="text-right text-white/90">{signal.city || t.notAvailable}</span>
        </div>
        <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
          <span className="text-white/45">{t.statusLabel}</span>
          <span className="text-right text-white/90">{signal.status || t.unknown}</span>
        </div>
      </div>
    </div>
  );
}

function MarketSignalListingTab({
  signal,
  t,
}: {
  signal: LeasingMarketSignalDTO;
  t: typeof translations.en;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold text-white/40">
          <FileText size={12} />
          {t.listingDescription}
        </div>
        <div className="max-h-[420px] overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-white/5 bg-white/[0.02] p-4 text-sm leading-relaxed text-white/70">
          {signal.listingDescription || signal.summary || t.noDescription}
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold text-white/40">
          <ListChecks size={12} />
          {t.amenitiesFeatures}
        </div>
        <div className="flex flex-wrap gap-2">
          {parseAmenities(signal.amenities).length > 0 ? (
            parseAmenities(signal.amenities).map((item, index) => (
              <span key={`${item}-${index}`} className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-white/70">
                {item}
              </span>
            ))
          ) : (
            <span className="text-xs italic text-white/30">{t.noAmenities}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function MarketSignalIntegrityTab({
  signal,
  t,
  dateLocale,
}: {
  signal: LeasingMarketSignalDTO;
  t: typeof translations.en;
  dateLocale: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.2fr_2fr]">
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold text-white/40">
          <Calendar size={12} />
          {t.dataIntegrity}
        </div>
        <div className="space-y-1 text-[12px] text-white/45">
          <div>
            {t.sourceLabel}: <span className="text-white/70">{signal.sourceName || t.notAvailable}</span>
          </div>
          <div>
            {t.capturedLabel}: <span className="text-white/70">{new Date(signal.createdAt).toLocaleDateString(dateLocale)}</span>
          </div>
          <div>
            {t.refLabel}: <span className="text-white/70">{signal.listingReference || t.notAvailable}</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold text-white/40">
          <Info size={12} />
          {t.keyFacts}
        </div>
        <div className="max-h-[320px] overflow-y-auto rounded-xl border border-white/5 bg-white/[0.02] p-3 text-[12px] text-white/65">
          {signal.keyFacts && Object.keys(signal.keyFacts).length > 0 ? (
            <pre className="whitespace-pre-wrap break-words font-mono">{JSON.stringify(signal.keyFacts, null, 2)}</pre>
          ) : (
            <span className="text-white/30">{t.notAvailable}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export const MarketSignalPage = ({ signalId, className, hasEntityRowAbove = false }: MarketSignalPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const signal = useKernelStore(
    (state) => (state.inventory.MarketSignal.registry[signalId] as MarketSignalInventoryEntityState | undefined) ?? null
  );
  const locale = lang === 'fr' ? 'fr-FR' : 'en-US';
  const dateLocale = lang === 'fr' ? 'fr-FR' : 'en-GB';
  const activeTabCandidate = signal?.activeTab ?? signal?.runtime?.activeTab;
  const activeTab = normalizeMarketSignalWorkspaceTab(activeTabCandidate) ?? 'FOUNDATION';

  const handleTabChange = useCallback(
    (nextTab: MarketSignalRuntimeState['activeTab']) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('MarketSignal', signalId, { activeTab: nextTab });
    },
    [kernel, signalId]
  );

  useEffect(() => {
    if (!kernel.current) return;
    if (normalizeMarketSignalWorkspaceTab(activeTabCandidate)) return;
    kernel.current.patchEntityRuntimeState('MarketSignal', signalId, {
      activeTab: 'FOUNDATION',
    });
  }, [kernel, signalId, activeTabCandidate]);

  const tabs = useMemo(
    () => [
      { id: 'FOUNDATION' as const, label: t.foundation },
      { id: 'LISTING' as const, label: t.listing },
      { id: 'INTEGRITY' as const, label: t.integrity },
    ],
    [t.foundation, t.integrity, t.listing]
  );

  if (!signal) return <EntitySkeleton variant="detail" className={className} />;

  return (
    <div
      className={clsx(
        'flex min-h-[620px] flex-col rounded-2xl border border-[#2A2A2A] bg-[#1A1A1A] text-white',
        className
      )}
    >
      <EntityPageTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        sticky
      />

      <div className="border-b border-[#2A2A2A] px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-base font-medium">{signal.propertyName || signal.unitIdentifier || t.notAvailable}</div>
            <div className="mt-1 flex items-center gap-2 text-xs text-white/60">
              <MapPin size={12} />
              <span className="truncate">
                {signal.propertyAddress || signal.city || t.unknown}
                {signal.postalCode ? ` (${signal.postalCode})` : ''}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={clsx(
                'rounded-full border px-2 py-0.5 text-[10px] font-bold',
                signal.status === 'AVAILABLE'
                  ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-400'
                  : 'border-white/10 bg-white/5 text-white/40'
              )}
            >
              {signal.status || t.unknown}
            </span>
            {signal.listingUrl && (
              <a
                href={signal.listingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <ExternalLink size={12} />
                {t.visitListing}
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {activeTab === 'FOUNDATION' && <MarketSignalFoundationTab signal={signal} t={t} locale={locale} />}
        {activeTab === 'LISTING' && <MarketSignalListingTab signal={signal} t={t} />}
        {activeTab === 'INTEGRITY' && <MarketSignalIntegrityTab signal={signal} t={t} dateLocale={dateLocale} />}
      </div>
    </div>
  );
};


